import { GoogleGenAI, Type } from "@google/genai";
import { CreativeBrief, CampaignConcept } from "../types";

/**
 * Generates a prompt based on the creative brief provided by the user.
 */
const synthesizePrompt = (brief: CreativeBrief): string => {
  const anatomicalParts = brief.gender === 'female' 
    ? `Body Logic: ${brief.bustLine} torso, ${brief.waistRatio} midsection, ${brief.hipsSculpt} legs.`
    : `Body Logic: ${brief.makeupPalette} grooming style, ${brief.waistRatio} core, ${brief.hipsSculpt} physique.`;

  const segments = [
    `Subject: ${brief.gender} digital twin.`,
    `Likeness Requirement: 100% facial accuracy to reference photos.`,
    `Aesthetic Vibe: ${brief.vibe}, viral meme energy.`,
    `Absurd Location: ${brief.scene}.`,
    `Ridiculous Wardrobe: ${brief.outfit}.`,
    `Lighting: ${brief.lighting}.`,
    `Lens Type: ${brief.lens}.`,
    `Photography Style: ${brief.realismTuning}.`,
    brief.hairStyle !== "As in photo" ? `Hair Detail: ${brief.hairStyle}.` : "",
    `Skin Finish: ${brief.skinStyle}.`,
    anatomicalParts,
    `Camera Perspective: ${brief.cameraAngle}.`,
    brief.customPrompt ? `Additional Chaos: ${brief.customPrompt}.` : ""
  ].filter(Boolean);

  return segments.join(" ");
};

/**
 * Creates campaign concepts based on the provided brief and anchor images.
 */
export const generateCampaignConcepts = async (brief: CreativeBrief, apiKey?: string): Promise<CampaignConcept[]> => {
  // Use provided key or fallback to environment variable
  const key = apiKey || process.env.API_KEY;
  if (!key) throw new Error("KEY_RESET_REQUIRED");

  const ai = new GoogleGenAI({ apiKey: key });
  const synthPrompt = synthesizePrompt(brief);

  const systemInstruction = `YOU ARE A VIRAL MEME ARCHITECT. 
  Storyboard 1 hilariously unexpected meme concept for a ${brief.gender} virtual twin.
  THE KEY TO VIRALITY IS A REALISTIC FACE IN AN ABSURD SITUATION. 
  FOCUS ON: High-impact "what am I looking at" energy, surreal environments, and ridiculous outfits.
  STRICT RULE: Do NOT describe the subject's facial features in the prompt. The identity is provided by the anchor images.
  Output JSON ARRAY ONLY with 1 item.`;

  const parts: any[] = [{ text: `BASE DIRECTIVE: ${synthPrompt}` }];
  
  Object.values(brief.anchorImages).forEach((img) => {
    if (img) {
      const mime = img.split(';')[0].split(':')[1] || 'image/jpeg';
      parts.push({ inlineData: { mimeType: mime, data: img.split(',')[1] } });
    }
  });

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-pro-preview',
      contents: { parts },
      config: {
        systemInstruction,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              id: { type: Type.STRING },
              themeIndex: { type: Type.NUMBER },
              title: { type: Type.STRING },
              description: { type: Type.STRING },
              prompt: { type: Type.STRING },
            },
            required: ["id", "themeIndex", "title", "description", "prompt"],
          },
        },
      },
    });

    const text = response.text || "[]";
    return JSON.parse(text.trim());
  } catch (error: any) {
    // Handle specific error code for key reset as per instructions
    if (error.message?.includes("Requested entity was not found")) {
      throw new Error("KEY_RESET_REQUIRED");
    }
    throw error;
  }
};

/**
 * Generates the final image of the twin in the selected meme context.
 */
export const generateTwinImage = async (
  brief: CreativeBrief,
  conceptPrompt: string,
  apiKey?: string
): Promise<string> => {
  // Use provided key or fallback to environment variable
  const key = apiKey || process.env.API_KEY;
  if (!key) throw new Error("KEY_RESET_REQUIRED");

  const ai = new GoogleGenAI({ apiKey: key });
  const model = 'gemini-3-pro-image-preview';
  
  const parts: any[] = [];
  const activeAnchors = Object.values(brief.anchorImages).filter(img => img !== null);
  
  activeAnchors.forEach((img) => {
    const mime = img!.split(';')[0].split(':')[1] || 'image/jpeg';
    parts.push({ inlineData: { mimeType: mime, data: img!.split(',')[1] } });
  });

  const anatomyRef = brief.gender === 'female' 
    ? `${brief.bustLine} torso, ${brief.waistRatio} midsection, ${brief.hipsSculpt} legs`
    : `${brief.makeupPalette} grooming, ${brief.waistRatio} core, ${brief.hipsSculpt} physique`;

  const finalPrompt = `
    CRITICAL IDENTITY REQUIREMENT: The attached photos are the PERMANENT FACE REFERENCES. 
    THE FACE MUST BE A PHOTOREALISTIC, HIGH-FIDELITY, 1:1 REPLICA OF THE REFERENCE PHOTOS. 
    PRESERVE: Eye shape, jawline, nose structure, and distinct facial features perfectly.
    
    TASK: Re-render this EXACT PERSON as the main character in an absurd, viral internet meme.
    SCENE: ${conceptPrompt}. 
    
    ZONAL STYLING RULES:
    1. THE FACE: Keep ultra-clean, sharp, and high-resolution. No distortion or "deep frying" on the face.
    2. THE BODY & COSTUME: Apply chaos here. ${brief.bodyType}, ${anatomyRef}. Outfit: ${brief.outfit}.
    3. THE ENVIRONMENT: Apply the ${brief.realismTuning} profile and ${brief.vibe} vibe only to the background and lighting.
    
    TECHNICAL SPECS:
    - Realism Profile: ${brief.realismTuning}
    - Lighting: ${brief.lighting}
    - Lens/Optics: ${brief.lens}
    - Angle: ${brief.cameraAngle}
    
    Make it look like a real, high-quality photograph of the person in the most ridiculous possible scenario.
  `.trim();

  parts.push({ text: finalPrompt });

  try {
    const response = await ai.models.generateContent({
      model,
      contents: { parts },
      config: {
        imageConfig: {
          aspectRatio: brief.aspectRatio,
          imageSize: brief.imageSize || "1K"
        }
      },
    });

    const imagePart = response.candidates?.[0]?.content?.parts?.find(p => !!p.inlineData);
    if (imagePart?.inlineData) return `data:image/png;base64,${imagePart.inlineData.data}`;
    throw new Error("PRODUCTION_ERROR");
  } catch (error: any) {
    // Handle specific error code for key reset as per instructions
    if (error.message?.includes("Requested entity was not found")) {
      throw new Error("KEY_RESET_REQUIRED");
    }
    throw error;
  }
};